import { AuthUser } from '../models/user';
import {
  LOGIN_LOCAL_STORAGE_KEY,
  SESSION_STORAGE_ACTIVE_KEY,
  SESSION_STORAGE_JWT_TOKEN,
} from '../pages/Login/constants';

// Where the signed-in user comes from.
//
// The account details are kept in localStorage so a reload can rebuild the session
// without asking for the password again. Anyone can edit that storage from their own
// browser, so anything it says about what the user may do is worth nothing: setting
// `role` to ADMIN by hand used to open the administration screens.
//
// The role is therefore read from the token instead. Decoding it here does not verify
// it, and cannot: only the server holds the key. What it does is tie what the interface
// shows to what the server will actually accept, since editing the payload breaks the
// signature and every call made from those screens is then refused. The interface stops
// promising what the API would deny.

type TokenPayload = {
  sub?: string;
  email?: string;
  role?: string;
  can_issue_document?: boolean;
  can_archive_secure_document?: boolean;
  can_issue_unsecure_document?: boolean;
  exp?: number;
};

// base64url, and the payload may hold anything UTF-8.
const decodeSegment = (segment: string): unknown => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '=',
  );

  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return JSON.parse(new TextDecoder().decode(bytes));
};

export const readTokenPayload = (
  token: string | null,
): TokenPayload | null => {
  if (!token) return null;

  const segments = token.split('.');
  if (segments.length !== 3) return null;

  try {
    const payload = decodeSegment(segments[1]) as TokenPayload;

    // An expired token is refused by the server, so the interface should not act on it
    // either. `exp` is in seconds.
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) {
      return null;
    }

    return payload;
  } catch (_) {
    return null;
  }
};

// The user the interface should act on, or null when there is no usable token.
//
// The display name comes from localStorage, which is harmless: at worst someone renames
// themselves on their own screen. Everything the guards read comes from the token.
export const readSessionUser = (): AuthUser | null => {
  const payload = readTokenPayload(
    sessionStorage.getItem(SESSION_STORAGE_JWT_TOKEN),
  );

  if (!payload) return null;

  let stored: Partial<AuthUser> = {};

  try {
    stored = JSON.parse(localStorage.getItem(LOGIN_LOCAL_STORAGE_KEY) || '{}');
  } catch (_) {
    stored = {};
  }

  return {
    ...stored,
    id: payload.sub,
    email: payload.email || stored.email || '',
    role: (payload.role as AuthUser['role']) || 'USER',
    can_issue_document: payload.can_issue_document,
    can_archive_secure_document: payload.can_archive_secure_document,
    can_issue_unsecure_document: payload.can_issue_unsecure_document,
  };
};

// Signing out.
//
// Everything the login wrote is removed: the remembered account, the "session active"
// flag and the token itself. The token used to be left behind, so a signed-out tab kept
// sending a valid bearer with every call until it was closed. "Remember me" is a
// preference of the browser, not of the session, and stays.
export const clearSession = (): void => {
  localStorage.removeItem(LOGIN_LOCAL_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_ACTIVE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_JWT_TOKEN);
};
